import { useCallback, useRef, useState } from 'react';
import Loading from './Loading';
import { uploadToS3 } from '~/components/FileUpload';
import { businessApiRoute, post } from '~/lib/Api';
import * as Toast from '~/lib/Toast';

interface FileUploadImmediateProps {
    onChange?: (url: string) => void;
    className?: string;
    buttonClassName?: string;
    id?: string;
    accept?: string;
    disabled?: boolean;
    children?: React.ReactNode;
    multiple?: boolean;
    onMultipleChange?: (urls: string[]) => void;
    onUploadStateChange?: (isUploading: boolean) => void;
}

/**
 * Component that immediately uploads files to S3 and returns the view URL
 * Unlike FileUpload, this doesn't manage file state - it just uploads and returns URLs
 */
const FileUploadImmediate = ({
    onChange,
    onMultipleChange,
    onUploadStateChange,
    className = '',
    buttonClassName = '',
    id,
    accept = 'image/*',
    disabled = false,
    children,
    multiple = false,
}: FileUploadImmediateProps) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadCount, setUploadCount] = useState(0);

    const uploadFile = async (file: File): Promise<string | null> => {
        try {
            // Upload to S3
            const results = await uploadToS3(
                file,
                () => {},
                () => {},
                false
            );

            // Persist uploaded image to a permanent key via API
            const stored = await post(
                businessApiRoute('/storage/image'),
                { upload: results }
            );

            const key = stored?.key;
            if (!key) {
                throw new Error('Image upload failed: missing key');
            }

            const domain = import.meta.env.DEV ? 'http://localhost' : 'https://app.breely.com';
            return `${domain}/view-image/${key}`;
        } catch (error) {
            console.error('Upload failed:', error);
            Toast.error(`Failed to upload ${file.name}`);
            return null;
        }
    };

    const onFileSelected = useCallback(async () => {
        const el = fileInputRef.current;

        if (!el?.files || el.files.length === 0) {
            return;
        }

        const files = Array.from(el.files);
        const maxSizeInBytes = 10 * 1024 * 1024; // 10MB in bytes

        // Validate files
        const validFiles = files.filter(file => {
            if (accept && !file.type.match(accept.replace('*', '.*'))) {
                Toast.error(`${file.name} is not a valid file type`);
                return false;
            }
            if (file.size > maxSizeInBytes) {
                Toast.error(`${file.name} is too large (max 10MB)`);
                return false;
            }
            return true;
        });

        if (validFiles.length === 0) {
            el.value = '';
            return;
        }

        setIsUploading(true);
        setUploadCount(validFiles.length);
        onUploadStateChange?.(true);

        try {
            if (multiple && onMultipleChange) {
                // Upload all files concurrently
                const uploadPromises = validFiles.map(file => uploadFile(file));
                const urls = await Promise.all(uploadPromises);
                const successfulUrls = urls.filter((url): url is string => url !== null);

                if (successfulUrls.length > 0) {
                    onMultipleChange(successfulUrls);
                    Toast.success(`Uploaded ${successfulUrls.length} file${successfulUrls.length > 1 ? 's' : ''}`);
                }
            } else {
                // Upload single file
                const url = await uploadFile(validFiles[0]);
                if (url) {
                    onChange?.(url);
                    Toast.success('File uploaded successfully');
                }
            }
        } catch (error) {
            console.error('Upload error:', error);
            Toast.error('Failed to upload files');
        } finally {
            setIsUploading(false);
            setUploadCount(0);
            onUploadStateChange?.(false);
            // Clear the file input
            el.value = '';
        }
    }, [onChange, onMultipleChange, onUploadStateChange, accept, multiple]);

    return (
        <div className={`flex items-center ${className}`}>
            <label>
                <input
                    type="file"
                    id={id}
                    className="hidden"
                    ref={fileInputRef}
                    onChange={onFileSelected}
                    accept={accept}
                    disabled={disabled || isUploading}
                    multiple={multiple}
                />
                <div
                    className={`btn-light-pill inline-block cursor-pointer ${disabled || isUploading ? 'opacity-50 cursor-not-allowed' : ''} ${buttonClassName}`}
                >
                    {children || 'Upload'}
                </div>
                {isUploading && (
                    <div className="ml-2 inline-flex items-center text-sm text-gray-500">
                        <Loading className="mr-2" message={`Uploading ${uploadCount} file${uploadCount > 1 ? 's' : ''}...`} />
                    </div>
                )}
            </label>
        </div>
    );
};

export default FileUploadImmediate;

